import { coreApiClient } from "../../../shared/services/CoreApiClient";
import { announce, el, liveRegion, replaceChildrenWith } from "./dom";
import {
  agenda,
  blockHead,
  breakdowns,
  dashboards,
  directory,
  legend,
  reconciliation,
  timeProfile,
  viewBar,
  viewBarSkeleton
} from "./report";
import type { ApiResponse, ReportContext, SpeakerParticipationReport } from "./types";

/**
 * AB#9981. The Speaker Participation report as a SET of DataInsights.
 *
 * Each insight is a section of the record and renders on its own, so a clerk assembling a
 * report by hand can take just the directory, or just the reconciliation, without
 * inheriting the rest. The composed report is simply all five in reading order.
 *
 * <b>They share one fetch.</b> Five insights on one page must not be five round trips: the
 * first to ask starts the request and the rest await the same promise, keyed on the meeting
 * and on whether contact detail was asked for. Without this the report is five identical
 * queries against the same meeting, and the read-at stamps disagree with each other.
 *
 * The data comes from CityzenWeb, not from ApiCore directly, because this page is also the
 * surface a SHARED report is read through and a shared report has an anonymous reader. The
 * CityzenWeb route decides who may see the record and whether they may see contact details;
 * this file never decides either.
 */

const pending: Record<string, Promise<SpeakerParticipationReport>> = {};

function cacheKey(meetingId: number, shortCode: string): string {
  return meetingId + "|" + shortCode;
}

function fetchReport(meetingId: number, shortCode: string): Promise<SpeakerParticipationReport> {
  const key = cacheKey(meetingId, shortCode);
  const existing = pending[key];
  if (existing) return existing;

  const url =
    "/MeetingSpeakerParticipation/Get?id=" +
    encodeURIComponent(String(meetingId)) +
    (shortCode ? "&shortCode=" + encodeURIComponent(shortCode) : "");

  const request = fetch(url, { credentials: "same-origin" })
    .then((response) => {
      if (!response.ok) {
        const error = new Error("HTTP " + response.status) as Error & { status: number };
        error.status = response.status;
        throw error;
      }
      return response.json() as Promise<ApiResponse<SpeakerParticipationReport>>;
    })
    .then((envelope) => {
      // Every CityzenApiCore response is wrapped, and this route passes that envelope
      // straight through. Reading the body as the payload yields an object with none of
      // the fields the renderer expects.
      if (!envelope || !envelope.data) {
        throw new Error("The report payload was empty.");
      }
      return envelope.data;
    })
    .catch((error: unknown) => {
      // A failed fetch must not poison the cache: the retry button would then replay the
      // rejection forever without ever going back to the server.
      delete pending[key];
      throw error;
    });

  pending[key] = request;
  return request;
}

/** Reads the page context an insight container carries, all of it server-rendered. */
function contextFor(container: HTMLElement, meetingId: number): ReportContext {
  const root = document.querySelector<HTMLElement>("[data-report-context]");
  const data = root ? root.dataset : ({} as DOMStringMap);

  return {
    meetingId,
    customerName: data.customerName || "",
    signedInEmail: data.signedInEmail || "",
    timeZoneId: data.timeZoneId || "",
    directoryPageSize: Number(data.directoryPageSize || "12"),
    canExport: data.canExport === "true" && container.getAttribute("data-can-export") !== "false"
  };
}

/**
 * Five outcomes, each named for what the reader should DO about it. A shared report whose
 * link was revoked and a meeting that was deleted are different problems with different
 * answers, and "something went wrong" is neither.
 */
function describe(status: number): { title: string; detail: string } {
  switch (status) {
    case 401:
      return {
        title: "Your session expired",
        detail: "Sign in again and reopen this report. Nothing was lost."
      };
    case 403:
      return {
        title: "You do not have access to this report",
        detail: "Your account cannot see speaker participation for this meeting."
      };
    case 404:
      return {
        title: "This report is not available",
        detail:
          "The meeting may have been deleted, or the link it was shared through may have " +
          "been turned off."
      };
    case 502:
      return {
        title: "The report could not be built",
        detail: "The reporting service did not answer. Try again in a moment."
      };
    default:
      return {
        title: "The report could not be loaded",
        detail: "Try again in a moment. If it keeps happening, tell support."
      };
  }
}

function renderError(container: HTMLElement, error: unknown, retry: () => void): void {
  const status = (error as { status?: number })?.status ?? 0;
  const described = describe(status);

  const button = el("button", { type: "button", class: "spr-btn", text: "Try again" });
  button.addEventListener("click", retry);

  replaceChildrenWith(container, [
    el("div", {
      class: "spr spr-block spr-error",
      "data-testid": "speaker-report-error",
      role: "alert"
    }, [
      el("h2", { class: "spr-error-title", text: described.title }),
      el("p", { class: "spr-error-detail", text: described.detail }),
      button
    ])
  ]);
}

function skeleton(): HTMLElement {
  const rows: HTMLElement[] = [];
  for (let i = 0; i < 3; i++) rows.push(el("div", { class: "spr-skel-row" }));

  return el("div", {
    class: "spr-skeleton",
    "data-testid": "speaker-report-skeleton",
    // Not aria-busy on an ancestor of the document: that hides everything, including
    // sections a screen reader user has already started reading.
    "aria-hidden": "true"
  }, rows);
}

type SectionName = "overview" | "rosters" | "directory" | "breakdowns" | "timeProfile";

function build(
  section: SectionName,
  report: SpeakerParticipationReport,
  context: ReportContext,
  host: HTMLElement
): Array<Node | null> {
  switch (section) {
    case "overview": {
      const exportLive = liveRegion("speaker-report-export-live");
      return [
        viewBar(
          {
            root: host,
            context,
            report,
            // Export goes STRAIGHT to ApiCore with the admin's JWT, unlike the read.
            // It is admin-only by definition, so there is no anonymous caller to serve and
            // no reason to route it through CityzenWeb: the caller's own permissions are
            // what decide whether the CSV carries contact details, and only ApiCore can
            // resolve those.
            requestExport: context.canExport
              ? (recipientEmail: string) =>
                coreApiClient
                  .post(
                    `/api/meetings/${context.meetingId}/speaker-participation/exports`,
                    { recipientEmail }
                  )
                  .then(() => {
                    announce(exportLive, "Export queued for " + recipientEmail + ".");
                  })
              : null
          },
          exportLive
        ),
        // Dashboards FIRST, above the reconciliation table.
        //
        // They are the board's own published views of this meeting -- the thing a reader
        // who arrived from an agenda is most often here for -- and as a footer to the time
        // profile they sat below every roster, every directory page and every cross-tab.
        // A link nobody scrolls to is a link nobody follows.
        dashboards(report),
        reconciliation(report),
        exportLive
      ];
    }
    case "rosters":
      return [agenda(report, host)];
    case "directory":
      // Its own legend. The five insights render in independent containers, so a clerk
      // who embedded only this one would otherwise see channel-coloured chips with the
      // key sitting on an insight that is not on their page.
      return [legend(report), directory(report, host, context)];
    case "breakdowns":
      return [legend(report), breakdowns(report)];
    case "timeProfile":
      // Three blocks, one insight: how long speakers talked, and the same meeting along its
      // own clock. No sixth EmbedType -- five insights is the settled architecture, and the
      // chart layer registers exactly five.
      return [timeProfile(report)];
    default:
      return [];
  }
}

/**
 * Renders one section into one insight container.
 *
 * @param container the insight's own element, supplied by the chart layer.
 * @param meetingId the meeting the insight was created against.
 * @param shortCode the share code when the reader arrived through a shared link.
 */
/**
 * Remove the report shell's own caption for this insight, if the shell painted one.
 *
 * Scoped deliberately: it walks up to this insight's `.asset-item` and touches only the
 * caption inside it, so an insight of any other type on the same report keeps its title.
 */
function hideShellCaption(container: HTMLElement): void {
  const asset = container.closest(".asset-item");
  const caption = asset?.querySelector<HTMLElement>(".insight-title");
  if (!caption) return;

  caption.textContent = "";
  caption.hidden = true;
}

/**
 * One colour key per page, however many insights carry one.
 *
 * The agenda, directory and breakdowns insights each BUILD their own legend on purpose: any
 * one of them can be embedded alone, and an insight shown alone must still explain its
 * colours. When several share a page, the first legend in document order stays visible and
 * the later ones are hidden -- hidden rather than removed, so if the insight holding the
 * visible one is re-rendered (a retry after an error) this pass promotes the next one
 * instead of leaving the page with none.
 *
 * Runs after EVERY insight lands, because insights resolve independently and the one that
 * finishes first is not necessarily the first on the page.
 */
function showOneLegend(): void {
  const legends = document.querySelectorAll<HTMLElement>(
    '.spr[data-testid="speaker-report"] [data-testid="speaker-report-legend"]');

  for (let i = 0; i < legends.length; i += 1) {
    legends[i].hidden = i > 0;
    if (i > 0) legends[i].setAttribute("data-legend-shown-above", "true");
    else legends[i].removeAttribute("data-legend-shown-above");
  }
}

export function renderSection(
  section: SectionName,
  container: HTMLElement,
  meetingId: number,
  shortCode: string
): void {
  container.classList.add("spr");
  container.setAttribute("data-testid", "speaker-report");
  container.setAttribute("data-loaded", "false");

  // Drop the generic per-insight caption the report shell paints above every chart.
  //
  // Each of these five insights renders its own <h2> -- "Does it add up", "Agenda and who
  // signed up" -- so the shell's <h6> repeats it, and an <h6> sitting immediately above an
  // <h2> is a heading-order violation as well as a duplicate.
  //
  // It is also frequently WRONG on older reports: the caption is DataInsight.Title, stored
  // when the report was composed, so reports created before this insight was relabelled
  // still read "... : Overview". Removing it here fixes those without a data migration,
  // and leaves the stored Title intact for the admin Options editor, which binds to it.
  hideShellCaption(container);

  // This insight's header is placed BEFORE the payload arrives, in the same wrapper it
  // will occupy afterwards, so the arrival of content does not move it. It needs only the
  // server-rendered page context, so there is nothing to wait for. Without this the
  // skeleton is a stack of anonymous bars and every section below it jumps when the data
  // lands -- which is what "a skeleton in the document's shape" exists to prevent.
  //
  // "overview" is the WIRE value, fixed by chart-factory.js, and stays as it is. What the
  // insight renders is the reconciliation block: the "What happened" stat tiles it used to
  // lead with were not in the approved prototype and were removed.
  const loadingContext = contextFor(container, meetingId);
  replaceChildrenWith(
    container,
    section === "overview"
      ? [
          viewBarSkeleton(loadingContext),
          el("section", { class: "spr-block" }, [blockHead(loadingContext), skeleton()])
        ]
      : [skeleton()]
  );

  fetchReport(meetingId, shortCode).then(
    (report) => {
      const context = loadingContext;
      try {
        replaceChildrenWith(container, build(section, report, context, container));

        // Publish the availability flags as state. Which columns exist is decided by
        // them, so "the organisation column is missing" is only a defect when the flag
        // says the customer collects one -- and a test cannot tell those apart by
        // looking at the columns. Machine-readable state, not inferred from labels.
        for (const [flag, present] of Object.entries(report.availability)) {
          container.setAttribute(
            "data-availability-" + flag.replace(/[A-Z]/g, (c) => "-" + c.toLowerCase()),
            present ? "true" : "false"
          );
        }

        container.setAttribute("data-loaded", "true");
        showOneLegend();
      } catch (error) {
        // The data arrived and we could not draw it. Caught separately from the fetch on
        // purpose: reporting a render fault as a network fault sends the next person to
        // look at CORS for an hour while the endpoint answers 200.
        // eslint-disable-next-line no-console
        console.error("Speaker participation insight: rendering failed.", error);
        renderError(container, { status: -1 }, () =>
          renderSection(section, container, meetingId, shortCode));
        container.setAttribute("data-loaded", "error");
      }
    },
    (error: unknown) => {
      renderError(container, error, () =>
        renderSection(section, container, meetingId, shortCode));
      container.setAttribute("data-loaded", "error");
    }
  );
}

declare global {
  interface Window {
    SpeakerParticipationInsights?: {
      render: (
        section: SectionName,
        container: HTMLElement,
        meetingId: number,
        shortCode: string
      ) => void;
    };
  }
}

// The chart layer is a plain script, not a webpack module, so the only way it can reach
// this code is a global. UMD output already puts the bundle on window; this names the one
// function it needs.
window.SpeakerParticipationInsights = { render: renderSection };
